import { Component } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ITooltipAngularComp } from 'ag-grid-angular';
import { ITooltipParams } from 'ag-grid-community';

interface TooltipHeader {
  name: string;
  property: string;
}

interface TooltipDetail {
  label: string;
  value: string;
}

interface RowDetailsTooltipParams extends ITooltipParams {
  headers?: TooltipHeader[];
}

@Component({
  selector: 'app-row-tooltip',
  template: `
    <div
  class="row-details-tooltip"
  role="tooltip"
  aria-label="Row details"
  *ngIf="details.length !== 0"
>
  <div class="tooltip-header" >
    <span class="tooltip-header-icon" aria-hidden="true">
      <i class="bi bi-info-circle"></i>
    </span>

    <span class="tooltip-title">Details</span>
  </div>

  <div class="tooltip-body">
    <div
      class="detail-row"
      *ngFor="let detail of details; trackBy: trackByLabel"
    >
      <div class="detail-label">
        {{ detail.label }}
      </div>

      <div class="detail-value">
        {{ detail.value }}
      </div>
    </div>

    <!-- <div class="empty-details" *ngIf="details.length === 0">
      No row details available.
    </div> -->
  </div>
</div>
  `,
  styles: [`
:host {
  display: block;
  pointer-events: auto;
}

.row-details-tooltip {
  pointer-events: auto;
  cursor: default;
}

.row-details-tooltip {
  width: 360px;
  max-width: min(360px, calc(100vw - 32px));
  overflow: hidden;

  color: var(--ca-text-primary, #172033);
  background: var(--ca-surface, #ffffff);

  border: 1px solid var(--ca-border, #dfe4ec);
  border-radius: var(--ca-radius-md, 10px);

  box-shadow:
    0 8px 24px rgba(4, 27, 56, 0.16),
    0 2px 6px rgba(4, 27, 56, 0.08);

  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
}

.tooltip-header {
  display: flex;
  align-items: center;
  gap: 10px;

  padding: 14px 16px;

  color: #ffffff;
  background: var(--ca-primary, #0e2e80);

  border-bottom: 1px solid var(--ca-primary-dark, #0d2863);
}

.tooltip-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 30px;
  height: 30px;

  flex-shrink: 0;

  color: #ffffff;
  background: rgba(255, 255, 255, 0.16);

  border-radius: 8px;

  font-size: 16px;
}

.tooltip-title {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.tooltip-body {
  display: flex;
  flex-direction: column;
  gap: 0;

  max-height: 320px;
  overflow-y: auto;

  padding: 8px 16px;
}

.detail-row {
  display: grid;
  grid-template-columns: minmax(110px, 0.85fr) minmax(0, 1.15fr);
  align-items: start;
  gap: 16px;

  padding: 11px 0;

  border-bottom: 1px solid var(--ca-border, #dfe4ec);

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  color: var(--ca-text-secondary, #657087);

  font-size: 13px;
  font-weight: 500;

  overflow-wrap: anywhere;
}

.detail-value {
  color: var(--ca-text-primary, #172033);

  font-size: 14px;
  font-weight: 500;

  text-align: right;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.empty-details {
  padding: 18px 0;

  color: var(--ca-text-secondary, #657087);

  font-size: 14px;
  text-align: center;
}

@media (max-width: 480px) {
  .row-details-tooltip {
    width: 300px;
  }

  .detail-row {
    grid-template-columns: minmax(90px, 0.8fr) minmax(0, 1.2fr);
    gap: 10px;
  }
}
  `]
}

)
export class RowTooltipComponent implements ITooltipAngularComp {
  details: TooltipDetail[] = [];

  constructor(private readonly datePipe: DatePipe) {}

  agInit(params: RowDetailsTooltipParams): void {
    this.buildDetails(params);
  }

  refresh(params: RowDetailsTooltipParams): boolean {
    this.buildDetails(params);
    return true;
  }

  private buildDetails(params: RowDetailsTooltipParams): void {
    // tooltipValueGetter supplies the complete row object.
    const rowData = params.value;

    if (!rowData || typeof rowData !== 'object') {
      this.details = [];
      return;
    }

    const headers = params.headers ?? [];

    this.details = headers.map((header) => ({
      label: header.name,
      value: this.formatValue(header.property, rowData[header.property])
    }));
  }

  private formatValue(property: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (property === 'updatedAt') {
      const formattedDate = this.datePipe.transform(
        value as string | number | Date,
        'dd MMM yyyy, h:mm a'
      );

      return formattedDate ?? '—';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  trackByLabel(index: number, detail: TooltipDetail): string {
  return detail.label;
}
}